/**
 * Alliance section (spec section 57): Members, Growth, Activity, Roles.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DetectedEventDto } from '@kso/schemas';
import { useAlliance, useAllianceActivity, useAllianceMembers } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import {
  Badge,
  Card,
  PageHeader,
  Stat,
  Value,
} from '../../components/ui/primitives';
import { DataTable } from '../../components/ui/DataTable';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
  NotAvailableYet,
  QueryBoundary,
} from '../../components/ui/states';
import { FreshnessBadge } from '../../components/freshness/Freshness';
import { InferenceMark, SampleCount } from '../../components/inference/Inference';
import { formatCompact, formatDateTime, formatDelta, formatPercent } from '../../lib/format';
import { detectedEventLabel } from '../../lib/enumLabels';
import { usePlayerColumns } from '../players/playerColumns';
import { useT } from '../../i18n';

/** Shared guard: every alliance screen needs a configured core alliance. */
function useCoreAllianceGuard() {
  const core = useCoreContext();
  return core;
}

function NoCoreAlliance({ title }: { title: string }) {
  const t = useT();
  return (
    <>
      <PageHeader title={title} />
      <EmptyState
        title={t.common.noCoreAllianceTitle}
        body={t.alliance.noCoreAllianceBody}
        action={
          <Link className="btn btn--sm" to="/data/sync">
            {t.common.openSyncStatus}
          </Link>
        }
      />
    </>
  );
}

export function AllianceMembersPage() {
  const t = useT();
  const core = useCoreAllianceGuard();
  const members = useAllianceMembers(core.allianceId ?? undefined);
  // Needed only for member_count, so the roster can name the gap between what
  // the game reports and what the upstream can actually identify.
  const alliance = useAlliance(core.allianceId ?? undefined);
  const columns = usePlayerColumns();
  const [filter, setFilter] = useState('');

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (!core.allianceId) return <NoCoreAlliance title={t.alliance.members.title} />;

  const allianceLabel = core.allianceLabel ?? '';

  return (
    <>
      <PageHeader
        title={t.alliance.members.title}
        subtitle={t.alliance.members.subtitle(allianceLabel)}
        actions={
          <input
            type="search"
            placeholder={t.alliance.members.searchPlaceholder}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            aria-label={t.alliance.members.searchAria}
            style={{ width: 200 }}
          />
        }
      />

      <QueryBoundary
        query={members}
        loadingLabel={t.common.loadingRoster}
        isEmpty={(data) => data.members.length === 0}
        empty={
          <EmptyState
            title={t.alliance.members.emptyTitle}
            body={t.alliance.members.emptyBody}
          />
        }
      >
        {(data) => {
          const reported = alliance.data?.alliance.memberCount ?? null;
          const hasGap = reported !== null && reported > data.members.length;
          return (
            <>
              {/*
                The header above shows the alliance's own member_count, which is
                routinely larger than the roster we can list: Jeabs returns
                unidentified members as blank rows carrying power 0, and ingest
                drops them rather than polluting every average with 47 zeroes.
                Two disagreeing numbers on one page reads as "people left", so the
                gap is named explicitly instead of left to be inferred. Both
                locales keep "identified" and "reported" as separate counts for
                exactly that reason.
              */}
              <p className="muted small">
                {hasGap ? (
                  <>
                    {t.alliance.members.identifiedOf(data.members.length, reported)}{' '}
                    <strong>
                      {t.alliance.members.couldNotIdentify(reported - data.members.length)}
                    </strong>{' '}
                    {t.alliance.members.couldNotIdentifyWhy}
                  </>
                ) : (
                  t.alliance.members.identified(data.members.length)
                )}
              </p>
              <DataTable
                data={data.members}
                columns={columns}
                globalFilter={filter}
                initialSorting={[{ id: 'power', desc: true }]}
                emptyMessage={t.alliance.members.noSearchMatch}
              />
            </>
          );
        }}
      </QueryBoundary>
    </>
  );
}

export function AllianceGrowthPage() {
  const t = useT();
  const core = useCoreAllianceGuard();
  const alliance = useAlliance(core.allianceId ?? undefined);

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (!core.allianceId) return <NoCoreAlliance title={t.alliance.growth.title} />;

  return (
    <>
      <PageHeader
        title={t.alliance.growth.title}
        subtitle={t.alliance.growth.subtitle(core.allianceLabel ?? '')}
      />

      <QueryBoundary query={alliance} loadingLabel={t.alliance.growth.loading}>
        {(data) => (
          <div className="stack">
            <div className="row">
              <h2>
                <Value absent={t.common.unnamedAlliance}>{data.alliance.name}</Value>
              </h2>
              <FreshnessBadge
                label={t.freshness.sources.alliance}
                info={data.alliance.freshness}
              />
            </div>

            <div className="grid grid--stats">
              <Stat
                label={t.common.power}
                value={formatCompact(data.alliance.power)}
                foot={<span className="faint">{t.common.observed}</span>}
                absentLabel={t.common.notSynced}
              />
              <Stat
                label={t.common.members}
                value={
                  data.alliance.memberCount === null
                    ? null
                    : `${data.alliance.memberCount}${data.alliance.memberMax ? ` / ${data.alliance.memberMax}` : ''}`
                }
                absentLabel={t.common.notSynced}
              />
              <Stat
                label={t.common.kills}
                value={formatCompact(data.alliance.kills)}
                foot={<span className="faint">{t.common.observed}</span>}
                absentLabel={t.common.notSynced}
              />
              <Stat
                label={t.common.level}
                value={data.alliance.level}
                absentLabel={t.common.notSynced}
              />
            </div>

            {data.growth ? (
              <Card
                title={
                  <span className="row">
                    {t.alliance.growth.cardTitle} <InferenceMark />
                    <SampleCount
                      samples={data.growth.sampleCount}
                      noun={t.inference.nouns.snapshots}
                      threshold={3}
                    />
                  </span>
                }
              >
                <div className="grid grid--stats">
                  <Stat
                    label={t.alliance.growth.window24h}
                    value={formatDelta(data.growth.powerGrowth24h)}
                    absentLabel={t.common.notEnoughHistory}
                  />
                  <Stat
                    label={t.alliance.growth.window7d}
                    value={formatDelta(data.growth.powerGrowth7d)}
                    foot={formatPercent(data.growth.powerGrowthPct7d)}
                    absentLabel={t.common.notEnoughHistory}
                  />
                  <Stat
                    label={t.alliance.growth.window30d}
                    value={formatDelta(data.growth.powerGrowth30d)}
                    absentLabel={t.common.notEnoughHistory}
                  />
                  <Stat
                    label={t.alliance.growth.memberChange7d}
                    value={
                      data.growth.memberDelta7d === null
                        ? null
                        : `${data.growth.memberDelta7d > 0 ? '+' : ''}${data.growth.memberDelta7d}`
                    }
                    absentLabel={t.common.notEnoughHistory}
                  />
                </div>
                {data.growth.sampleCount < 3 ? (
                  <p className="small" style={{ color: 'var(--low)', marginBottom: 0 }}>
                    {t.alliance.growth.thinHistory(data.growth.sampleCount)}
                  </p>
                ) : null}
              </Card>
            ) : (
              <EmptyState
                title={t.alliance.growth.emptyTitle}
                body={t.alliance.growth.emptyBody}
              />
            )}
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

export function AllianceActivityPage() {
  const t = useT();
  const core = useCoreAllianceGuard();
  const activity = useAllianceActivity(core.allianceId ?? undefined);

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (!core.allianceId) return <NoCoreAlliance title={t.alliance.activity.title} />;

  return (
    <>
      <PageHeader title={t.alliance.activity.title} subtitle={t.alliance.activity.subtitle} />

      <QueryBoundary
        query={activity}
        loadingLabel={t.alliance.activity.loading}
        isEmpty={(data) => data.events.length === 0}
        empty={
          <EmptyState
            title={t.alliance.activity.emptyTitle}
            body={t.alliance.activity.emptyBody}
          />
        }
      >
        {(data) => <DetectedEventTable events={data.events} />}
      </QueryBoundary>
    </>
  );
}

export function DetectedEventTable({ events }: { events: DetectedEventDto[] }) {
  const t = useT();
  const columns = t.alliance.activity.columns;

  return (
    <>
      <p className="muted small">{t.alliance.activity.caveat}</p>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{columns.detected}</th>
              <th>{columns.event}</th>
              <th>{columns.player}</th>
              <th>{columns.alliance}</th>
              <th>{columns.relatedAlliance}</th>
              <th>{columns.confidence}</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td className="nowrap">{formatDateTime(event.detectedAt)}</td>
                <td>
                  <Badge tone="neutral">{detectedEventLabel(t, event.eventType)}</Badge>
                </td>
                <td>
                  {event.playerId ? (
                    <Link to={`/players/${encodeURIComponent(event.playerId)}`}>
                      <Value absent={t.common.unnamedPlayer}>{event.playerName}</Value>
                    </Link>
                  ) : (
                    <span className="absent">{t.common.notIdentified}</span>
                  )}
                </td>
                <td className="mono small">
                  {event.allianceId ?? <span className="absent">{t.common.none}</span>}
                </td>
                <td className="mono small">
                  {event.relatedAllianceId ?? <span className="absent">{t.common.dash}</span>}
                </td>
                <td>
                  {event.confidence === null ? (
                    <span className="absent">{t.common.notScored}</span>
                  ) : (
                    <Badge
                      tone={event.confidence >= 0.7 ? 'HIGH' : event.confidence >= 0.4 ? 'MEDIUM' : 'LOW'}
                    >
                      {Math.round(event.confidence * 100)}%
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function AllianceRolesPage() {
  const t = useT();
  return (
    <>
      <PageHeader title={t.alliance.roles.title} subtitle={t.alliance.roles.subtitle} />
      <NotAvailableYet
        feature={t.alliance.roles.feature}
        phase={t.alliance.roles.phase}
        describes={t.alliance.roles.describes}
      />
    </>
  );
}
